import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useAuth } from '@/context/AuthContext'
import { favoritesApi } from '@/api/favorites'
import { buildWarehouseScopedStorageKey } from '@/features/warehouse/config'
import { useWarehouse } from '@/context/WarehouseContext'

type FavoritesContextValue = {
  favoriteIds: string[]
  favoriteCount: number
  isFavorite: (id: string) => boolean
  toggleFavorite: (id: string) => void
  clearFavorites: () => void
  reloadFromServer: () => Promise<void>
}

const FAVORITES_STORAGE_KEY = 'nurm_favorites_v1'

const FavoritesContext = createContext<FavoritesContextValue | null>(null)

function loadFavoritesFromStorage(storageKey: string): string[] {
  try {
    const raw = localStorage.getItem(storageKey)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string' && Boolean(x)) : []
  } catch {
    return []
  }
}

export const FavoritesProvider = ({ children }: { children: ReactNode }) => {
  const { selectedWarehouseId } = useWarehouse()
  const { token, isAuthenticated } = useAuth()
  const favoritesStorageKey = buildWarehouseScopedStorageKey(
    FAVORITES_STORAGE_KEY,
    selectedWarehouseId
  )
  const [favoriteIds, setFavoriteIds] = useState<string[]>(() => loadFavoritesFromStorage(favoritesStorageKey))
  const [storageReadyKey, setStorageReadyKey] = useState<string | null>(favoritesStorageKey)

  // Гость: загрузка из localStorage при монтировании (если ещё не авторизован)
  useEffect(() => {
    if (isAuthenticated) return
    setFavoriteIds(loadFavoritesFromStorage(favoritesStorageKey))
    setStorageReadyKey(favoritesStorageKey)
  }, [isAuthenticated, favoritesStorageKey])

  // Гость: сохранение в localStorage
  useEffect(() => {
    if (isAuthenticated) return
    if (storageReadyKey !== favoritesStorageKey) return
    localStorage.setItem(favoritesStorageKey, JSON.stringify(favoriteIds))
  }, [isAuthenticated, favoriteIds, favoritesStorageKey, storageReadyKey])

  const reloadFromServer = useCallback(async () => {
    if (!token) return
    try {
      const ids = await favoritesApi.getFavorites()
      setFavoriteIds(ids.map(String))
    } catch {
      setFavoriteIds([])
    }
  }, [token])

  // Авторизован: загрузка с сервера
  useEffect(() => {
    if (!isAuthenticated || !token) return
    reloadFromServer()
  }, [isAuthenticated, token, reloadFromServer, selectedWarehouseId])

  const isFavorite = useCallback(
    (id: string) => favoriteIds.includes(id),
    [favoriteIds]
  )

  const toggleFavorite = useCallback(
    (id: string) => {
      const productId = Number(id)
      if (Number.isNaN(productId)) return

      if (token) {
        if (favoriteIds.includes(id)) {
          favoritesApi.remove(productId).then(() => {
            setFavoriteIds((prev) => prev.filter((item) => item !== id))
          }).catch(() => {})
        } else {
          favoritesApi.add(productId).then(() => {
            setFavoriteIds((prev) => [...prev, id])
          }).catch(() => {})
        }
        return
      }

      setFavoriteIds((prev) =>
        prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
      )
    },
    [token, favoriteIds]
  )

  const clearFavorites = useCallback(() => {
    const idsToClear = [...favoriteIds]
    // Оптимистично очищаем UI сразу
    setFavoriteIds([])

    if (!token) return

    const numericIds = idsToClear
      .map((x) => Number(x))
      .filter((n) => !Number.isNaN(n) && n > 0)

    Promise.all(
      numericIds.map((id) => favoritesApi.remove(id).catch(() => null))
    )
      .then(() => {
        // На всякий случай синхронизируем с сервером
        return reloadFromServer()
      })
      .catch(() => {
        void reloadFromServer()
      })
  }, [favoriteIds, token, reloadFromServer])

  const value = useMemo(
    () => ({
      favoriteIds,
      favoriteCount: favoriteIds.length,
      isFavorite,
      toggleFavorite,
      clearFavorites,
      reloadFromServer,
    }),
    [
      favoriteIds,
      isFavorite,
      toggleFavorite,
      clearFavorites,
      reloadFromServer,
    ]
  )

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  )
}

export const useFavorites = () => {
  const ctx = useContext(FavoritesContext)
  if (!ctx) {
    throw new Error('useFavorites must be used within FavoritesProvider')
  }
  return ctx
}
